import { Accidental, Formatter, Modifier, RenderContext } from "vexflow";
import { StaveNote } from "../common/extension/MusiconStaveNote";
import Stave from "../common/extension/MusiconStave";
import { Interval, Note, distance } from "tonal";

export interface StaveOption {
  clef?: string;
  width: number;
  x: number;
  y: number;
}

export interface KeyRange {
  max: string;
  min: string;
}

export const getTouchPositionInCanvas = (
  canvas: any,
  evt: Position
): Position => {
  //pegando apenas os valores de border e padding (eliminando coisas como cor e caracteres de pixel 'px'
  // var border = getStyle(canvas.id, 'border-left-width');
  // var borderValue = border!=null?border.substring(0, border.indexOf(border.match(/\D/))):0;
  const borderValue = 0;
  // var padding = getStyle(canvas.id, 'padding-left');
  // var paddingValue = padding!=null?padding.substring(0, padding.indexOf(padding.match(/\D/))):0;
  const paddingValue = 0;
  const rect = canvas.getBoundingClientRect();

  return {
    x: evt.x - rect.left - borderValue - paddingValue,
    y: evt.y - rect.top - borderValue - paddingValue,
  };
};

export interface Position {
  x: number;
  y: number;
}

export const findWhichStaveMouseOver = (
  staveList: Stave[],
  mousePos: Position
) => {
  for (var i = 0; i < staveList.length; i++) {
    var box = staveList[i].getBoundingBox();
    if (
      isCursorWithinRectangle(
        box.x,
        box.y,
        box.w,
        box.h,
        mousePos.x,
        mousePos.y
      )
    )
      return staveList[i];
  }
  return null;
};

const isCursorWithinRectangle = (
  x: number,
  y: number,
  width: number,
  height: number,
  mouseX: number,
  mouseY: number
) => {
  if (mouseX > x && mouseX < x + width && mouseY > y && mouseY < y + height) {
    return true;
  }
  return false;
};

export const addNote = (
  stave: Stave,
  context: RenderContext,
  callback?: (notes: StaveNote[]) => void
) => {
  const notes = [
    { keys: ["C/4"], duration: "4" },
    { keys: ["D/4"], duration: "4" },
    { keys: ["E/4"], duration: "4" },
    { keys: ["F/4"], duration: "4" },
  ]
    .map(
      ({ keys, duration }) =>
        new StaveNote({
          keys,
          duration,
        })
    )
    .map((note) => note.setStave(stave).setContext(context));

  callback?.call(this, notes);

  Formatter.FormatAndDraw(context, stave, notes);
};

export const getStemDirection = (stave: Stave, y: number) => {
  var reference = stave.getYForLine(2);

  if (y < reference) return StaveNote.STEM_DOWN;
  else return StaveNote.STEM_UP;
};

export const deepClone = (obj: any) => {
  let objProto = Object.getPrototypeOf(obj);
  // @ts-ignore
  return Object.assign(Object.create(objProto), obj);
};

/**
 *
 * @param obj1 The first object
 * @param obj2 The second object
 * @returns A new object representing the merged objects. If both objects passed as param have the same prop, then obj2 property is returned.
 */
export const mergeProperties = (obj1: any, obj2: any) => {
  return Object.assign({}, obj1, obj2);
};

export const countDuration = (duration: string): number => {
  const durationMap = new Map();
  durationMap.set("1", 1);
  durationMap.set("2", 2);
  durationMap.set("4", 4);
  durationMap.set("8", 8);
  durationMap.set("16", 16);
  durationMap.set("32", 32);
  durationMap.set("64", 64);
  durationMap.set("w", 1);
  durationMap.set("h", 2);
  durationMap.set("q", 4);

  return durationMap.get(duration);
};

export const formatStavesToJson = (staves: Stave[]) => {
  return staves
    .filter((stave: Stave) => {
      return stave.getTickables().length > 0;
    })
    .map((stave: Stave) => {
      return {
        notes: stave.getTickables().map((note: StaveNote) => {
          const accidentals = getAccidentals(note);

          return {
            keys: note.getKeys(),
            duration: note.getDuration(),
            accidentals,
          };
        }),
      };
    });
};

export const getAccidentals = (note: StaveNote): any[] => {
  return note
    .getModifiers()
    .filter((modifier: Modifier) => {
      return modifier instanceof Accidental;
    })
    .map((modifier: Modifier) => {
      const index = modifier.getIndex();

      if (modifier instanceof Accidental) {
        return { modifier: modifier.type, index };
      }

      return null;
    });
};

export const groupSequence = (notes: StaveNote[] = []): StaveNote[][] => {
  const sequence = notes.map(
    (note: StaveNote) => 1 / countDuration(note.getDuration())
  );
  const groups: number[][] = [];
  let currentGroup: number[] = [];

  const noteGroups: StaveNote[][] = [];
  let currentNoteGroup: StaveNote[] = [];
  let preDuration = countDuration(notes[0]?.getDuration());

  for (let i = 0; i < sequence.length; i++) {
    if (
      currentGroup.reduce((sum, num) => sum + num, 0) + sequence[i] > 1 ||
      preDuration != countDuration(notes[i].getDuration()) // 一个小节只允许一种音符
    ) {
      groups.push(currentGroup);
      noteGroups.push(currentNoteGroup);
      currentGroup = [sequence[i]];
      currentNoteGroup = [notes[i]];
    } else {
      currentGroup.push(sequence[i]);
      currentNoteGroup.push(notes[i]);
    }
    preDuration = countDuration(notes[i].getDuration());
  }

  if (currentGroup.length > 0) {
    groups.push(currentGroup);
    noteGroups.push(currentNoteGroup);
  }

  return noteGroups;
};

export const transposePitch = (
  pitch: string,
  direction: string = "up",
  range: KeyRange = { max: "G/6", min: "B/2" }
): string => {
  const tonalPitch = convertNoteFormat(pitch);

  let dst = "0";
  if (direction == "up") {
    dst = pitch.includes("E") || pitch.includes("B") ? "2m" : "2M";
  } else {
    dst = pitch.includes("F") || pitch.includes("C") ? "-2m" : "-2M";
  }

  let newKey = pitch;
  const newPitch = Note.transpose(tonalPitch, dst);

  if (newKey != "" && isInRange(newPitch, range)) {
    newKey = convertNoteFormat(newPitch);
  }

  return newKey;
};

const isInRange = (newPitch: string, range: { max: string; min: string }) => {
  let semMax = Interval.semitones(
    distance(newPitch, convertNoteFormat(range.max))
  );
  let semMin = Interval.semitones(
    distance(convertNoteFormat(range.min), newPitch)
  );

  semMax = semMax ? semMax : 0;
  semMin = semMin ? semMin : 0;

  return semMax >= 0 && semMin >= 0;
};

// TypeScript
export const convertNoteFormat = (note: string): string => {
  if (note.includes("/")) {
    // if input is in C/4 format
    let [noteLetter, noteOctave] = note.split("/");
    return `${noteLetter}${noteOctave}`; // convert to C4 format
  } else {
    // assume input is in C4 format
    let noteLetter = note.slice(0, -1);
    let noteOctave = note.slice(-1);
    return `${noteLetter}/${noteOctave}`; // Convert to C/4 format
  }
};

export const getMoveKeysFunc = (
  action: string,
  currentNote: StaveNote | null,
  range: KeyRange
) => {
  return (stave: Stave) => {
    if (!checkCurrentStave(stave, currentNote)) {
      return stave;
    }

    const notes = stave.getTickables();
    const newNotes = notes.map((note: StaveNote) => {
      if (!checkCurrentNote(note, currentNote)) {
        return note;
      }

      const newKeys = note.getKeys().map((key: string) => {
        let newKey = key;
        try {
          newKey = transposePitch(key, action, range);
        } catch (e) {
          console.log(e);
        }
        return newKey;
      });

      note.keys = uniqueStringArray(newKeys);

      const newNote = new StaveNote({
        keys: newKeys,
        duration: note.getDuration(),
      });

      const stave = note.getStave();

      if (stave) newNote.setStave(stave);

      newNote.autoStem();
      getAccidentals(note).map((accidental: string) => {
        newNote.addModifier(new Accidental(accidental));
      });

      return newNote;
    });

    stave.setTickables(newNotes);

    return stave;
  };
};

export const checkCurrentStave = (
  stave: Stave,
  currentNote: StaveNote | null
): boolean => {
  if (!currentNote) {
    return true;
  }

  const curStaveBox = currentNote.getStave()?.getBoundingBox();
  const staveBox = stave.getBoundingBox();

  try {
    if (
      curStaveBox?.getX() != staveBox.getX() ||
      curStaveBox?.getY() != staveBox.getY()
    ) {
      return false;
    }
  } catch (e) {
    console.log(e);
    return false;
  }

  return true;
};

export const checkCurrentNote = (
  note: StaveNote,
  currentNote: StaveNote | null
): boolean => {
  if (!currentNote) {
    return true;
  }

  const curNoteBox = currentNote.getBoundingBox();
  const noteBox = note?.getBoundingBox();

  try {
    if (curNoteBox.getX() != noteBox.getX()) {
      return false;
    }
  } catch (e) {
    console.log(e);
    return false;
  }

  return true;
};

export const uniqueStringArray = (arr: string[]): string[] => {
  return Array.from(new Set(arr));
};

export const getUrlParams = (): URLSearchParams => {
  const params = new URLSearchParams(window.location.search);

  return params;
};

export const getUrlParam = (key: string): string | null => {
  const params = new URLSearchParams(window.location.search);

  return params.get(key);
};

export const noteObjToStr = (note: any) => {
  if (!note) return ''
  const key = note.keys[0].split('/')[0];
  const octave = parseInt(note.keys[0].split('/')[1]);
  const accidentals = note.accidentals[0] ?? ''
  return `${key}${accidentals}${octave}`
}

export const generateRandomArray = (length: number) => {
  const naturalNumbers = Array.from({ length: length }, (_, i) => i + 1);

  // 打乱数组顺序
  for (let i = naturalNumbers.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [naturalNumbers[i], naturalNumbers[j]] = [naturalNumbers[j], naturalNumbers[i]];
  }

  return naturalNumbers;
}


/**
 * 五线谱相同位置高音、低音区转化层
 * @param note 例如：C4, A5, Bb4, D#2 等等
 * @param type clef_to_base | base_to_clef
 */
export const clefBassTransform = (note: string, type: string = 'clef_to_bass'): string => {

  let acc = ''
  let _note: any
  if (note.length === 3) {
    acc = note[1]
  }

  if (acc) {
    _note = note.split(acc)
    note = `${_note[0]}${_note[1]}`
  }

  const clefs = [
    'A6', 'B6', 'C7', 'D7', 'E7', 'F7', 'G7',
    'A5', 'B5', 'C6', 'D6', 'E6', 'F6', 'G6',
    'G5', 'F5', 'E5', 'D5', 'C5', 'B4', 'A4',
    'G4', 'F4', 'E4', 'D4', 'C4', 'B3', 'A3',
    'G3', 'F3', 'E3', 'D3', 'C3', 'B2'
  ]
  const basses = [
    'C5', 'D5', 'E5', 'F5', 'G5', 'B5', 'A5',
    'C4', 'D4', 'E4', 'F4', 'G4', 'A4', 'B4',
    'B3', 'A3', 'G3', 'F3', 'E3', 'D3', 'C3',
    'B2', 'A2', 'G2', 'F2', 'E2', 'D2', 'C2',
    'B1', 'A1', 'G1', 'F1', 'E1', 'D1'
  ]

  try {
    const index = type === 'clef_to_bass' ? clefs.indexOf(note) : basses.indexOf(note)

    let noteTransformed = type === 'clef_to_bass' ? basses[index] : clefs[index]

    if (acc) {
      noteTransformed = noteTransformed.substring(0, 1) + acc + noteTransformed.substring(1)
    }

    return noteTransformed

  } catch (e) {
    console.log('高低音区转化失败:', e)
    // debug前为了不使页面崩溃
    return 'C4'
  }
}
